import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { useMe } from '../../hooks/useMe';
import { confirm3ds, dismiss3ds, type Card } from '../../lib/cards';

interface Props {
  card: Card;
  onDone: () => void;
}

export function ThreeDsModal({ card, onDone }: Props) {
  const { t } = useTranslation();
  const { initData } = useMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Unmount on success/swipe so BottomSheet releases sheetCount (footer).
  const [closed, setClosed] = useState(false);
  const [copied, setCopied] = useState(false);

  const method = card.pendingSecurementMethod ?? 'OOB';
  const isOtp = method === 'OTP' || method === 'VERIFICATION';
  const isVerification = method === 'VERIFICATION';
  const otpCode = card.pendingOtpValue;
  const securementId = card.pendingSecurementId;
  const amount = card.pendingSecurementAmount;

  const title = isVerification ? t('cards.verificationTitle') : t('cards.3dsTitle');
  const desc = isVerification
    ? t('cards.verificationDesc')
    : isOtp
      ? t('cards.3dsOtpDesc')
      : t('cards.3dsDesc');

  function finish() {
    setClosed(true);
    onDone();
  }

  async function handleOob(action: 'ACCEPTED' | 'DECLINED') {
    if (busy || !securementId) return;
    setBusy(true);
    setError(null);
    try {
      await confirm3ds(initData, card.id, securementId, action);
      finish();
    } catch {
      setError(t('cards.3dsError'));
      setBusy(false);
    }
  }

  async function handleDismiss() {
    if (busy) return;
    setBusy(true);
    try {
      await dismiss3ds(initData, card.id);
    } catch {
      /* still release UI */
    }
    finish();
  }

  // Await clear/decline so optimistic onDone isn't undone by a refetch racing
  // a fire-and-forget call while pending is still in the DB.
  async function handleSheetClose() {
    if (busy) return;
    setBusy(true);
    finish();
    try {
      if (isOtp) await dismiss3ds(initData, card.id);
      else if (securementId) await confirm3ds(initData, card.id, securementId, 'DECLINED');
    } catch {
      /* 404 clears server-side; other errors may restore on next poll */
    }
  }

  async function copyCode() {
    if (!otpCode) return;
    try {
      await navigator.clipboard.writeText(otpCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  if (closed) return null;

  return (
    <BottomSheet open onClose={() => void handleSheetClose()} dismissible={!busy} title={title}>
      <div className="flex flex-col gap-4 px-4 pb-6 pt-2">
        <div className="rounded-[20px] bg-[#f6f7f8] px-5 py-4 text-center">
          <p className="text-[13px] leading-relaxed text-muted">{desc}</p>

          {isOtp && otpCode && (
            <button
              onClick={() => void copyCode()}
              className="mt-3 font-mono text-[40px] font-bold tracking-[0.15em] text-ink active:opacity-60"
            >
              {otpCode}
            </button>
          )}
          {isOtp && otpCode && (
            <p className="mt-1 text-[12px] text-muted">
              {copied ? t('common.copied') : t('common.copy')}
            </p>
          )}

          {!isOtp && amount && (
            <p className="mt-2 text-[28px] font-semibold text-ink">{amount}</p>
          )}

          <p className="mt-1 text-[13px] text-muted">
            {[card.label?.trim() || card.name, card.last4 ? `•••• ${card.last4}` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        {error && (
          <p className="text-center text-[13px] text-red-500">{error}</p>
        )}

        {isOtp ? (
          <button
            onClick={() => void handleDismiss()}
            disabled={busy}
            className="flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-40"
          >
            {t('common.done')}
          </button>
        ) : (
          <>
            <button
              onClick={() => void handleOob('ACCEPTED')}
              disabled={busy}
              className="flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-40"
            >
              {busy ? '...' : t('cards.3dsApprove')}
            </button>
            <button
              onClick={() => void handleOob('DECLINED')}
              disabled={busy}
              className="flex h-12 w-full items-center justify-center rounded-full text-[15px] font-medium text-red-500 active:opacity-70 disabled:opacity-40"
            >
              {t('cards.3dsDecline')}
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
